import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { ComponentPriceTable } from "@/components/pricing-components/ComponentPriceTable";
import { ComponentPriceForm } from "@/components/pricing-components/ComponentPriceForm";
import { PricingTierTable } from "@/components/pricing-tiers/PricingTierTable";
import { PricingTierForm } from "@/components/pricing-tiers/PricingTierForm";
import { usePricingComponent } from "@/hooks/usePricingComponents";
import { useComponentPrices } from "@/hooks/useComponentPrices";
import { usePricingTiers } from "@/hooks/usePricingTiers";
import {
  BILLING_FREQUENCIES,
  type BillingFrequency,
  type ComponentPriceListDto,
} from "@/types/component-price";
import type { ComponentType } from "@/types/pricing-component";
import type { PricingTierDetailDto } from "@/types/pricing-tier";

// ============================================================================
// Helpers
// ============================================================================

function getComponentTypeBadgeVariant(
  type: ComponentType,
): "default" | "secondary" | "outline" {
  switch (type) {
    case "Resource":
      return "default";
    case "Addon":
      return "secondary";
    case "Service":
    case "Usage":
      return "outline";
    default:
      return "outline";
  }
}

// ============================================================================
// Component
// ============================================================================

export function PricingComponentDetailPage() {
  // Prices are readable by every admin role; adding one is SuperAdmin+BillingAdmin.
  const { hasRole } = usePermissions();
  const canEditCatalog = hasRole(["SuperAdmin", "BillingAdmin"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Filter states
  const [region, setRegion] = useState<string>("All");
  const [currency, setCurrency] = useState<string>("All");
  const [billingFrequency, setBillingFrequency] = useState<
    BillingFrequency | "All"
  >("All");
  const [isActive, setIsActive] = useState<"All" | "Active" | "Inactive">(
    "All",
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal states for prices
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);

  // Modal states for tiers
  const [isTiersDialogOpen, setIsTiersDialogOpen] = useState(false);
  const [isCreateTierModalOpen, setIsCreateTierModalOpen] = useState(false);
  const [isEditTierModalOpen, setIsEditTierModalOpen] = useState(false);
  const [selectedPriceForTiers, setSelectedPriceForTiers] =
    useState<ComponentPriceListDto | null>(null);
  const [selectedTier, setSelectedTier] = useState<
    PricingTierDetailDto | undefined
  >();

  // Fetch component details
  const { data: component, isLoading: isLoadingComponent } =
    usePricingComponent(id);

  // Build filter object for prices
  const priceFilters = {
    region: region === "All" ? undefined : region,
    currency: currency === "All" ? undefined : currency,
    billingFrequency: billingFrequency === "All" ? undefined : billingFrequency,
    isActive: isActive === "All" ? undefined : isActive === "Active",
    page: page,
    limit: pageSize,
  };

  // Fetch component prices
  const { data: priceResponse, isLoading: isLoadingPrices } =
    useComponentPrices(id, priceFilters);

  const prices = priceResponse?.data || [];
  const totalPages = priceResponse?.pagination?.totalPages || 1;
  const currentPage = priceResponse?.pagination?.currentPage || 1;
  const totalCount = priceResponse?.pagination?.totalItems || 0;

  const startItem = prices.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Fetch pricing tiers for selected price
  const { data: tiers = [], isLoading: isLoadingTiers } = usePricingTiers(
    selectedPriceForTiers?.id || "",
  );

  // Event handlers
  const handleEdit = (price: ComponentPriceListDto) => {
    setEditingPriceId(price.id);
    setIsEditModalOpen(true);
  };

  const handleViewTiers = (price: ComponentPriceListDto) => {
    setSelectedPriceForTiers(price);
    setIsTiersDialogOpen(true);
  };

  const handleEditTier = (tier: PricingTierDetailDto) => {
    setSelectedTier(tier);
    setIsEditTierModalOpen(true);
  };

  const handleCloseTiersDialog = () => {
    setIsTiersDialogOpen(false);
    setSelectedPriceForTiers(null);
    setSelectedTier(undefined);
  };

  // Loading state
  if (isLoadingComponent) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!component && !isLoadingComponent) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Component Not Found"
          subtitle="The requested pricing component does not exist"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Button onClick={() => navigate("/pricing-components")}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Components
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={component?.name ?? "Pricing Component"}
        subtitle="Manage prices for this pricing component"
        back={{
          label: "Back to Components",
          onClick: () => navigate("/admin/pricing/components"),
        }}
        actions={
          !canEditCatalog
            ? []
            : [
                {
                  label: "Add Price",
                  onClick: () => setIsCreateModalOpen(true),
                  icon: <Plus className="mr-2 h-4 w-4" />,
                },
              ]
        }
      />

      {/* Component Info Card */}
      {component && (
        <Card>
          <CardHeader>
            <CardTitle>Component Details</CardTitle>
            <CardDescription>
              Information about this pricing component
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Slug
                </p>
                <p className="text-lg font-mono">{component.slug}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Unit
                </p>
                <p className="text-lg">{component.unit}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Type
                </p>
                <Badge
                  variant={getComponentTypeBadgeVariant(
                    component.componentType,
                  )}
                  className="text-lg"
                >
                  {component.componentType}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Status
                </p>
                <Badge
                  variant={component.isActive ? "default" : "destructive"}
                  className="text-lg"
                >
                  {component.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Prices
                </p>
                <p className="text-lg font-semibold">
                  {component.priceCount || 0}
                </p>
              </div>
            </div>
            {component.description && (
              <div className="mt-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Description
                </p>
                <p className="text-base mt-1">{component.description}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter component prices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Region Filter */}
            <Select
              value={region}
              onValueChange={(value) => {
                setRegion(value);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Regions</SelectItem>
                <SelectItem value="Global">Global</SelectItem>
                <SelectItem value="US-East-1">US-East-1</SelectItem>
                <SelectItem value="US-West-1">US-West-1</SelectItem>
                <SelectItem value="EU-West-1">EU-West-1</SelectItem>
                <SelectItem value="EU-Central-1">EU-Central-1</SelectItem>
                <SelectItem value="AP-Southeast-1">AP-Southeast-1</SelectItem>
              </SelectContent>
            </Select>

            {/* Currency Filter */}
            <Select
              value={currency}
              onValueChange={(value) => {
                setCurrency(value);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Currencies</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="JPY">JPY</SelectItem>
                <SelectItem value="VND">VND</SelectItem>
              </SelectContent>
            </Select>

            {/* Billing Frequency Filter */}
            <Select
              value={billingFrequency}
              onValueChange={(value) => {
                setBillingFrequency(value as BillingFrequency | "All");
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Billing Frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Frequencies</SelectItem>
                {BILLING_FREQUENCIES.map((freq) => (
                  <SelectItem key={freq} value={freq}>
                    {freq}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select
              value={isActive}
              onValueChange={(value) => {
                setIsActive(value as "All" | "Active" | "Inactive");
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Prices Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Component Prices ({totalCount})</CardTitle>
              <CardDescription>
                Showing {startItem}-{endItem} of {totalCount} prices
              </CardDescription>
            </div>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(parseInt(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="20">20 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ComponentPriceTable
            prices={prices}
            isLoading={isLoadingPrices}
            onEdit={handleEdit}
            onViewTiers={handleViewTiers}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Price Modal */}
      {component && (
        <ComponentPriceForm
          key="create"
          open={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
          componentId={component.id}
          componentName={component.name}
          mode="create"
        />
      )}

      {/* Edit Price Modal */}
      {component && (
        <ComponentPriceForm
          key={editingPriceId ?? "edit"}
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          componentId={component.id}
          componentName={component.name}
          priceId={editingPriceId ?? undefined}
          mode="edit"
        />
      )}

      {/* Pricing Tiers Dialog */}
      <Dialog
        open={isTiersDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseTiersDialog();
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pricing Tiers</DialogTitle>
          </DialogHeader>

          <DialogBody withoutFooter className="space-y-4">
            <DialogDescription>
              {selectedPriceForTiers && (
                <>
                  Volume-based pricing tiers for{" "}
                  <span className="font-semibold">
                    {selectedPriceForTiers.componentName}
                  </span>{" "}
                  ({selectedPriceForTiers.region || "Global"} -{" "}
                  {selectedPriceForTiers.currency})
                </>
              )}
            </DialogDescription>
            {/* Add Tier Button */}
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {tiers.length === 0
                  ? "No tiers configured"
                  : `${tiers.length} tier${tiers.length === 1 ? "" : "s"} configured`}
              </div>
              <Button
                size="sm"
                onClick={() => setIsCreateTierModalOpen(true)}
                disabled={!selectedPriceForTiers}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Tier
              </Button>
            </div>

            {/* Tiers Table */}
            <PricingTierTable
              tiers={tiers}
              isLoading={isLoadingTiers}
              currency={selectedPriceForTiers?.currency || "USD"}
              onEdit={handleEditTier}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Create Tier Modal */}
      {selectedPriceForTiers && (
        <PricingTierForm
          open={isCreateTierModalOpen}
          onOpenChange={setIsCreateTierModalOpen}
          componentPriceId={selectedPriceForTiers.id}
          currency={selectedPriceForTiers.currency}
          mode="create"
        />
      )}

      {/* Edit Tier Modal */}
      {selectedPriceForTiers && selectedTier && (
        <PricingTierForm
          open={isEditTierModalOpen}
          onOpenChange={setIsEditTierModalOpen}
          componentPriceId={selectedPriceForTiers.id}
          currency={selectedPriceForTiers.currency}
          tier={selectedTier}
          mode="edit"
        />
      )}
    </div>
  );
}
